import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Building2, Users, GraduationCap, TrendingUp, 
  ChevronRight, ChevronDown, BarChart3, School, MapPin, Globe, UserCog,
  ArrowUp, ArrowDown, Minus, AlertTriangle, Award, Target, BookOpen,
  Headphones, Mic, Eye as EyeIcon, PenTool
} from 'lucide-react';
import axios from 'axios';
import UserManagement from '@/components/UserManagement';
import AssignmentManager from '@/components/AssignmentManager';
import ProficiencySpiderChart from '@/components/ProficiencySpiderChart';
import LicenseManager from '@/components/LicenseManager';
import DistrictSidebar from '@/components/DistrictSidebar';
import InstallAppBanner from '@/components/InstallAppBanner';
import MosaicDistrictRollup from '@/components/mosaic/MosaicDistrictRollup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WIDA_LEVEL_NAMES = {1: 'Entering', 2: 'Emerging', 3: 'Developing', 4: 'Expanding', 5: 'Bridging', 6: 'Reaching'};

const STATUS_CONFIG = {
  exceeding: { label: 'Exceeding', color: 'bg-green-100 text-green-800 border-green-300', dot: 'bg-green-500' },
  meeting: { label: 'Meeting', color: 'bg-blue-100 text-blue-800 border-blue-300', dot: 'bg-blue-500' },
  approaching: { label: 'Approaching', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', dot: 'bg-yellow-500' },
  needs_support: { label: 'Needs Support', color: 'bg-red-100 text-red-800 border-red-300', dot: 'bg-red-500' },
  no_data: { label: 'No Data', color: 'bg-gray-100 text-gray-500 border-gray-300', dot: 'bg-gray-400' },
};

const MODALITY_ICONS = {
  listening: Headphones,
  speaking: Mic,
  reading: EyeIcon,
  writing: PenTool,
};

const DistrictAdminDashboard = () => {
  const { user } = useAuth();
  const [districts, setDistricts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [stats, setStats] = useState(null);
  const [schoolPerformance, setSchoolPerformance] = useState(null);
  const [modalityComparison, setModalityComparison] = useState(null);
  const [gradePerformance, setGradePerformance] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [expandedSchool, setExpandedSchool] = useState(null);
  const [sortBy, setSortBy] = useState('rank');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [spiderData, setSpiderData] = useState([]);
  const [benchmarks, setBenchmarks] = useState({ listening: 3.0, speaking: 3.0, reading: 3.0, writing: 3.0 });
  const [editingBenchmarks, setEditingBenchmarks] = useState(false);
  const [benchmarkForm, setBenchmarkForm] = useState({ listening: 3.0, speaking: 3.0, reading: 3.0, writing: 3.0 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [districtsRes, schoolsRes] = await Promise.all([
          axios.get(`${API}/districts`).catch(() => ({ data: { districts: [] } })),
          axios.get(`${API}/schools`).catch(() => ({ data: { schools: [] } }))
        ]);
        
        setDistricts(districtsRes.data.districts || []);
        setSchools(schoolsRes.data.schools || []);
        
        if (districtsRes.data.districts?.length > 0) {
          const allDistricts = districtsRes.data.districts;
          // Default to user's district if they have one, otherwise first
          const userDistrict = user?.district_id 
            ? allDistricts.find(d => d.district_id === user.district_id) 
            : null;
          const firstDistrict = userDistrict || allDistricts[0];
          setSelectedDistrict(firstDistrict);
          await loadDistrictData(firstDistrict.district_id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  // Fetch spider chart data and benchmarks
  useEffect(() => {
    axios.get(`${API}/analytics/spider/district`).then(res => {
      setSpiderData(res.data.schools || []);
    }).catch(() => {});
    axios.get(`${API}/analytics/district-benchmarks`).then(res => {
      setBenchmarks(res.data);
      setBenchmarkForm(res.data);
    }).catch(() => {});
  }, []);

  const loadDistrictData = async (districtId) => {
    const [statsRes, perfRes, modRes, gradeRes] = await Promise.all([
      axios.get(`${API}/districts/${districtId}/stats`).catch(() => ({ data: {} })),
      axios.get(`${API}/districts/${districtId}/school-performance`).catch(() => ({ data: { schools: [], district_summary: {} } })),
      axios.get(`${API}/districts/${districtId}/modality-comparison`).catch(() => ({ data: { comparison: [] } })),
      axios.get(`${API}/districts/${districtId}/grade-performance`).catch(() => ({ data: { grades: [] } })),
    ]);
    setStats(statsRes.data);
    setSchoolPerformance(perfRes.data);
    setModalityComparison(modRes.data);
    setGradePerformance(gradeRes.data);
  };

  const selectDistrict = async (district) => {
    setSelectedDistrict(district);
    setLoading(true);
    await loadDistrictData(district.district_id);
    setLoading(false);
  };

  const sortedSchools = () => {
    if (!schoolPerformance?.schools) return [];
    const arr = [...schoolPerformance.schools];
    switch (sortBy) {
      case 'rank': return arr.sort((a, b) => a.rank - b.rank);
      case 'name': return arr.sort((a, b) => a.school_name.localeCompare(b.school_name));
      case 'students': return arr.sort((a, b) => b.students - a.students);
      case 'growth': return arr.sort((a, b) => b.growth_trend - a.growth_trend);
      case 'needs_support': return arr.sort((a, b) => a.avg_overall - b.avg_overall);
      default: return arr;
    }
  };

  const getProficiencyLevelColor = (level) => {
    const colors = {
      1: 'bg-red-100 text-red-700',
      2: 'bg-orange-100 text-orange-700',
      3: 'bg-yellow-100 text-yellow-700',
      4: 'bg-blue-100 text-blue-700',
      5: 'bg-indigo-100 text-indigo-700',
      6: 'bg-green-100 text-green-700'
    };
    return colors[level] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const summary = schoolPerformance?.district_summary || {};

  return (
    <div className="max-w-[1600px] mx-auto px-4 lg:px-6 pt-16 md:pt-8 pb-8 md:flex md:gap-8" data-testid="district-admin-dashboard">
      {/* v6.32.102 — Left sidebar nav (replaces v6.32.101 horizontal 8-tab strip). v6.34.14 — breakpoint lowered from lg≥1024 to md≥768 so tablets get the full sidebar. */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="md:flex md:gap-8 md:flex-1 md:items-start">
        <DistrictSidebar activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <Globe size={16} />
              <span>District Administration</span>
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedDistrict?.name || 'District Dashboard'}
            </h1>
            <p className="text-gray-600">
              Managing {summary.total_schools || schools.length} schools, {summary.total_students || 0} students, {summary.total_teachers || 0} teachers
            </p>
          </div>

          {/* v6.32.166 — MOSAIC District Rollup */}
          <div className="mb-8">
            <MosaicDistrictRollup />
          </div>

          {/* District Selector */}
          {districts.length > 1 && (
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {districts.map(district => (
                <Button
                  key={district.district_id}
                  variant={selectedDistrict?.district_id === district.district_id ? "default" : "outline"}
                  onClick={() => selectDistrict(district)}
                  className={`btn-sharp whitespace-nowrap ${
                    selectedDistrict?.district_id === district.district_id ? 'btn-sharp-primary' : ''
                  }`}
                >
                  {district.name}
                </Button>
              ))}
            </div>
          )}

          {/* Top-Level Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-t border-l border-gray-300 mb-8">
            <div className="swiss-card cursor-pointer hover:bg-blue-50 transition-colors" onClick={() => setActiveTab('school-performance')} data-testid="stat-schools">
              <Building2 className="w-7 h-7 text-blue-600 mb-2" />
              <div className="text-3xl font-bold">{summary.total_schools || 0}</div>
              <div className="text-xs text-gray-500">Schools</div>
            </div>
            <div className="swiss-card cursor-pointer hover:bg-green-50 transition-colors" onClick={() => { setUserRoleFilter('student'); setActiveTab('users'); }} data-testid="stat-students">
              <Users className="w-7 h-7 text-green-600 mb-2" />
              <div className="text-3xl font-bold">{summary.total_students || 0}</div>
              <div className="text-xs text-gray-500">Students</div>
            </div>
            <div className="swiss-card cursor-pointer hover:bg-purple-50 transition-colors" onClick={() => { setUserRoleFilter('teacher'); setActiveTab('users'); }} data-testid="stat-teachers">
              <GraduationCap className="w-7 h-7 text-purple-600 mb-2" />
              <div className="text-3xl font-bold">{summary.total_teachers || 0}</div>
              <div className="text-xs text-gray-500">Teachers</div>
            </div>
            <div className="swiss-card cursor-pointer hover:bg-orange-50 transition-colors" onClick={() => setActiveTab('by-grade')} data-testid="stat-assessments">
              <BarChart3 className="w-7 h-7 text-orange-600 mb-2" />
              <div className="text-3xl font-bold">{summary.total_exams || 0}</div>
              <div className="text-xs text-gray-500">Assessments</div>
            </div>
            <div className="swiss-card cursor-pointer hover:bg-indigo-50 transition-colors" onClick={() => setActiveTab('wida')} data-testid="stat-district-avg">
              <TrendingUp className="w-7 h-7 text-[#002FA7] mb-2" />
              <div className="text-3xl font-bold text-[#002FA7]">{summary.district_avg_overall || 0}</div>
              <div className="text-xs text-gray-500">District Avg Proficiency</div>
            </div>
          </div>

          {/* Status Summary Badges */}
          {summary.total_schools > 0 && (
            <div className="flex flex-wrap gap-3 mb-8" data-testid="status-summary">
              {[
                { key: 'exceeding', count: summary.schools_exceeding },
                { key: 'meeting', count: summary.schools_meeting },
                { key: 'approaching', count: summary.schools_approaching },
                { key: 'needs_support', count: summary.schools_needs_support },
              ].map(({ key, count }) => {
                const cfg = STATUS_CONFIG[key];
                return (
                  <div key={key} className={`flex items-center gap-2 px-4 py-2 border ${cfg.color}`} data-testid={`status-${key}`}>
                    <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                    <span className="font-semibold text-sm">{count}</span>
                    <span className="text-sm">{cfg.label}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* TabsList retained as a hidden anchor so Radix internals + any
              external selectors keep working. The visible navigation is
              rendered by <DistrictSidebar/>. */}
          <TabsList className="sr-only" aria-hidden="true">
            <TabsTrigger value="overview" data-testid="da-overview-tab-anchor">Overview</TabsTrigger>
            <TabsTrigger value="school-performance" data-testid="school-performance-tab-anchor">School Performance</TabsTrigger>
            <TabsTrigger value="modality" data-testid="modality-tab-anchor">Modality Analysis</TabsTrigger>
            <TabsTrigger value="by-grade" data-testid="by-grade-tab-anchor">By Grade Level</TabsTrigger>
            <TabsTrigger value="wida" data-testid="da-wida-tab-anchor">Proficiency Distribution</TabsTrigger>
            <TabsTrigger value="users" data-testid="user-management-tab-anchor">User Management</TabsTrigger>
            <TabsTrigger value="assignments" data-testid="assignments-tab-anchor">Assignments</TabsTrigger>
            <TabsTrigger value="licenses" data-testid="licenses-tab-anchor">Licenses</TabsTrigger>
          </TabsList>

          <div className="space-y-6">

        {/* ═══════════════ Overview Tab ═══════════════ */}
        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-8">
            {/* School Rankings */}
            <div className="border-2 border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-bold" data-testid="school-rankings-title">School Rankings</h3>
                <p className="text-sm text-gray-500">Ranked by average MOSAIC composite proficiency score</p>
              </div>
              <div className="divide-y divide-gray-100">
                {schoolPerformance?.schools?.map((school, idx) => {
                  const statusCfg = STATUS_CONFIG[school.status] || STATUS_CONFIG.no_data;
                  return (
                    <div key={school.school_id} className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => { setActiveTab('school-performance'); setExpandedSchool(school.school_id); }}
                      data-testid={`school-rank-${idx}`}
                    >
                      <div className="w-10 h-10 bg-gray-100 flex items-center justify-center font-bold text-lg text-gray-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {school.rank}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{school.school_name}</div>
                        <div className="text-xs text-gray-500">{school.students} students &bull; {school.total_exams} exams</div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className={`px-2 py-0.5 text-xs font-medium border ${statusCfg.color}`}>
                          {statusCfg.label}
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-[#002FA7]">{school.avg_overall}</div>
                          <div className="text-xs text-gray-400 flex items-center gap-0.5 justify-end">
                            {school.vs_district > 0 ? <ArrowUp size={10} className="text-green-600" /> : school.vs_district < 0 ? <ArrowDown size={10} className="text-red-500" /> : <Minus size={10} />}
                            {Math.abs(school.vs_district)} vs avg
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* District Modality Overview + Quick Actions */}
            <div className="space-y-8">
              {/* Spider Chart — School Comparison */}
              <div className="border-2 border-gray-200">
                <ProficiencySpiderChart
                  series={spiderData.filter(s => s.listening || s.speaking || s.reading || s.writing).map(s => ({ name: s.school_name, ...s }))}
                  targets={benchmarks}
                  title="School Modality Comparison"
                  subtitle="Each school's average MOSAIC proficiency scores with district targets (dashed red)"
                  height={300}
                />
              </div>

              {/* District Benchmark Targets */}
              <div className="border-2 border-gray-200">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-sm">District Benchmark Targets</h3>
                    <p className="text-xs text-gray-400">Set target scores per modality (shown as dashed line on chart)</p>
                  </div>
                  {!editingBenchmarks && (
                    <Button size="sm" variant="outline" className="text-xs h-7" style={{ borderRadius: 0 }}
                      onClick={() => { setEditingBenchmarks(true); setBenchmarkForm(benchmarks); }}
                      data-testid="edit-benchmarks-btn"
                    >
                      <Target size={12} className="mr-1" /> Edit
                    </Button>
                  )}
                </div>
                <div className="p-4">
                  {editingBenchmarks ? (
                    <div className="space-y-3">
                      {['listening', 'speaking', 'reading', 'writing'].map(mod => (
                        <div key={mod} className="flex items-center gap-3">
                          <span className="text-sm font-medium capitalize w-20">{mod}</span>
                          <input
                            type="number" min="1" max="6" step="0.1"
                            value={benchmarkForm[mod]}
                            onChange={e => setBenchmarkForm({ ...benchmarkForm, [mod]: parseFloat(e.target.value) || 0 })}
                            className="w-20 h-8 border-2 border-gray-300 px-2 text-sm text-center"
                            data-testid={`benchmark-${mod}`}
                          />
                          <span className="text-xs text-gray-400">/ 6.0</span>
                        </div>
                      ))}
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" className="bg-[#002FA7] text-white text-xs h-8" style={{ borderRadius: 0 }}
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/analytics/district-benchmarks`, benchmarkForm);
                              setBenchmarks(benchmarkForm);
                              setEditingBenchmarks(false);
                              // toast would need import, using alert
                            } catch {}
                          }}
                          data-testid="save-benchmarks-btn"
                        >Save Targets</Button>
                        <Button size="sm" variant="outline" className="text-xs h-8" style={{ borderRadius: 0 }}
                          onClick={() => setEditingBenchmarks(false)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {['listening', 'speaking', 'reading', 'writing'].map(mod => (
                        <div key={mod} className="flex items-center gap-2">
                          <span className="text-sm capitalize w-16 text-gray-500">{mod}</span>
                          <span className="font-bold text-sm text-[#002FA7]">{benchmarks[mod]}</span>
                          <span className="text-xs text-gray-300">/ 6</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="border-2 border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-bold">Administration</h3>
                </div>
                <div className="p-4 space-y-3">
                  <Link to="/results" className="block">
                    <Button variant="outline" className="w-full btn-sharp justify-start h-12">
                      <BarChart3 className="mr-3" size={18} />
                      District-Wide Results
                    </Button>
                  </Link>
                  <Link to="/teacher-key" className="block">
                    <Button variant="outline" className="w-full btn-sharp justify-start h-12">
                      <GraduationCap className="mr-3" size={18} />
                      Assessment Materials
                    </Button>
                  </Link>
                  <Link to="/grades" className="block">
                    <Button className="w-full btn-sharp-primary justify-start h-12">
                      <School className="mr-3" size={18} />
                      View Assessments
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ═══════════════ School Performance Tab ═══════════════ */}
        <TabsContent value="school-performance" data-testid="school-performance-content">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>School Performance Comparison</h3>
              <p className="text-sm text-gray-500">Click any school to see detailed breakdown</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Sort by:</span>
              {[
                { key: 'rank', label: 'Rank' },
                { key: 'name', label: 'Name' },
                { key: 'students', label: 'Size' },
                { key: 'growth', label: 'Growth' },
                { key: 'needs_support', label: 'Needs Support First' },
              ].map(s => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key)}
                  className={`text-xs px-3 py-1.5 border transition-colors ${sortBy === s.key ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                  data-testid={`sort-${s.key}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {sortedSchools().map((school) => {
              const statusCfg = STATUS_CONFIG[school.status] || STATUS_CONFIG.no_data;
              const isExpanded = expandedSchool === school.school_id;
              const distAvg = summary.district_avg_overall || 0;

              return (
                <div key={school.school_id} className={`border-2 transition-colors ${isExpanded ? 'border-[#002FA7]' : 'border-gray-200 hover:border-gray-400'}`} data-testid={`school-card-${school.school_id}`}>
                  {/* School Header */}
                  <div
                    className="p-5 cursor-pointer flex items-center gap-4"
                    onClick={() => setExpandedSchool(isExpanded ? null : school.school_id)}
                  >
                    {/* Rank Badge */}
                    <div className={`w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 ${
                      school.rank === 1 ? 'bg-yellow-100 text-yellow-700' :
                      school.rank === 2 ? 'bg-gray-200 text-gray-600' :
                      school.rank === 3 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-500'
                    }`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                      #{school.rank}
                    </div>

                    {/* School Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-base">{school.school_name}</h4>
                        <span className={`px-2 py-0.5 text-xs font-medium border ${statusCfg.color}`}>
                          {statusCfg.label}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-3 mt-1">
                        <span>DBN: {school.dbn || 'N/A'}</span>
                        <span>{school.students} students</span>
                        <span>{school.teachers} teachers</span>
                        <span>{school.total_exams} exams</span>
                      </div>
                    </div>

                    {/* Score + Growth */}
                    <div className="flex items-center gap-6 flex-shrink-0">
                      {/* Domain mini-bars */}
                      <div className="hidden md:flex items-end gap-1 h-10">
                        {['listening', 'speaking', 'reading', 'writing'].map(mod => {
                          const val = school[`avg_${mod}`] || 0;
                          const pct = (val / 6) * 100;
                          return (
                            <div key={mod} className="flex flex-col items-center" title={`${mod}: ${val}`}>
                              <div className="w-5 bg-gray-100 relative" style={{ height: '40px' }}>
                                <div
                                  className={`absolute bottom-0 w-full ${mod === 'listening' ? 'bg-cyan-400' : mod === 'speaking' ? 'bg-purple-400' : mod === 'reading' ? 'bg-blue-400' : 'bg-orange-400'}`}
                                  style={{ height: `${pct}%` }}
                                />
                              </div>
                              <span className="text-[9px] text-gray-400 mt-0.5 capitalize">{mod[0].toUpperCase()}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Overall Score */}
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#002FA7]" style={{ fontFamily: 'Outfit, sans-serif' }}>{school.avg_overall}</div>
                        <div className="text-xs text-gray-400">MOSAIC Avg</div>
                      </div>

                      {/* vs District */}
                      <div className="text-center">
                        <div className={`flex items-center gap-1 font-semibold text-sm ${school.vs_district > 0 ? 'text-green-600' : school.vs_district < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                          {school.vs_district > 0 ? <ArrowUp size={14} /> : school.vs_district < 0 ? <ArrowDown size={14} /> : <Minus size={14} />}
                          {school.vs_district > 0 ? '+' : ''}{school.vs_district}
                        </div>
                        <div className="text-xs text-gray-400">vs District</div>
                      </div>

                      {/* Growth */}
                      <div className="text-center">
                        <div className={`flex items-center gap-1 font-semibold text-sm ${school.growth_trend > 0 ? 'text-green-600' : school.growth_trend < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                          {school.growth_trend > 0 ? <TrendingUp size={14} /> : school.growth_trend < 0 ? <ArrowDown size={14} /> : <Minus size={14} />}
                          {school.growth_trend > 0 ? '+' : ''}{school.growth_trend}
                        </div>
                        <div className="text-xs text-gray-400">Growth</div>
                      </div>

                      <ChevronDown size={18} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t-2 border-gray-200 bg-gray-50 p-6" data-testid={`school-detail-${school.school_id}`}>
                      <div className="grid md:grid-cols-3 gap-6">
                        {/* Domain Scores */}
                        <div className="border border-gray-200 bg-white p-4">
                          <h5 className="font-bold text-sm mb-3">Domain Scores vs District Average</h5>
                          <div className="space-y-3">
                            {['listening', 'speaking', 'reading', 'writing'].map(mod => {
                              const Icon = MODALITY_ICONS[mod];
                              const schoolVal = school[`avg_${mod}`] || 0;
                              const districtVal = summary[`district_avg_${mod}`] || 0;
                              const diff = (schoolVal - districtVal).toFixed(1);
                              return (
                                <div key={mod} className="flex items-center gap-2">
                                  <Icon size={14} className="text-gray-500 flex-shrink-0" />
                                  <span className="text-xs capitalize w-16">{mod}</span>
                                  <div className="flex-1 h-2 bg-gray-100 relative">
                                    {/* District average marker */}
                                    <div className="absolute top-0 h-full w-0.5 bg-gray-400 z-10" style={{ left: `${(districtVal / 6) * 100}%` }} title={`District: ${districtVal}`} />
                                    <div className={`h-full ${schoolVal >= districtVal ? 'bg-green-400' : 'bg-red-300'}`} style={{ width: `${(schoolVal / 6) * 100}%` }} />
                                  </div>
                                  <span className="text-xs font-bold w-8 text-right">{schoolVal}</span>
                                  <span className={`text-[10px] w-10 text-right ${parseFloat(diff) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                    {parseFloat(diff) > 0 ? '+' : ''}{diff}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* WIDA Distribution */}
                        <div className="border border-gray-200 bg-white p-4">
                          <h5 className="font-bold text-sm mb-3">Proficiency Level Distribution</h5>
                          <div className="space-y-2">
                            {[1, 2, 3, 4, 5, 6].map(level => {
                              const count = school.wida_distribution?.[level] || 0;
                              const total = school.total_exams || 1;
                              const pct = Math.round((count / total) * 100);
                              return (
                                <div key={level} className="flex items-center gap-2">
                                  <div className={`w-6 h-6 flex items-center justify-center text-xs font-bold ${getProficiencyLevelColor(level)}`}>{level}</div>
                                  <div className="flex-1 h-2 bg-gray-100">
                                    <div className={`h-full ${level <= 2 ? 'bg-red-300' : level <= 4 ? 'bg-yellow-300' : 'bg-green-300'}`} style={{ width: `${pct}%` }} />
                                  </div>
                                  <span className="text-xs text-gray-500 w-16 text-right">{count} ({pct}%)</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Grade Breakdown */}
                        <div className="border border-gray-200 bg-white p-4">
                          <h5 className="font-bold text-sm mb-3">Performance by Grade</h5>
                          {school.by_grade && Object.keys(school.by_grade).length > 0 ? (
                            <div className="space-y-2">
                              {Object.entries(school.by_grade).map(([grade, data]) => (
                                <div key={grade} className="flex items-center gap-2">
                                  <span className="text-xs w-16">Gr. {grade}</span>
                                  <div className="flex-1 h-2 bg-gray-100">
                                    <div className="h-full bg-[#002FA7]" style={{ width: `${(data.avg_score / 6) * 100}%` }} />
                                  </div>
                                  <span className="text-xs font-bold w-8 text-right">{data.avg_score}</span>
                                  <span className="text-[10px] text-gray-400 w-14 text-right">{data.count} exams</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 text-center py-4">No grade data</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* ═══════════════ Modality Analysis Tab ═══════════════ */}
        <TabsContent value="modality" data-testid="modality-content">
          <div className="mb-6">
            <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Modality Comparison Across Schools</h3>
            <p className="text-sm text-gray-500">Identify where each school excels and where additional support is needed</p>
          </div>

          {/* Modality Comparison Table */}
          <div className="border-2 border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 bg-gray-50">
                  <th className="text-left p-3 font-bold">School</th>
                  <th className="p-3 text-center font-bold">
                    <div className="flex items-center justify-center gap-1"><Headphones size={14} /> Listening</div>
                  </th>
                  <th className="p-3 text-center font-bold">
                    <div className="flex items-center justify-center gap-1"><Mic size={14} /> Speaking</div>
                  </th>
                  <th className="p-3 text-center font-bold">
                    <div className="flex items-center justify-center gap-1"><EyeIcon size={14} /> Reading</div>
                  </th>
                  <th className="p-3 text-center font-bold">
                    <div className="flex items-center justify-center gap-1"><PenTool size={14} /> Writing</div>
                  </th>
                  <th className="p-3 text-center font-bold">Strongest</th>
                  <th className="p-3 text-center font-bold">Weakest</th>
                </tr>
              </thead>
              <tbody>
                {modalityComparison?.comparison?.map((school, idx) => (
                  <tr key={school.school_id} className={`border-b border-gray-100 ${idx % 2 === 0 ? '' : 'bg-gray-50'}`} data-testid={`modality-row-${idx}`}>
                    <td className="p-3 font-medium">{school.school_name}</td>
                    {['listening', 'speaking', 'reading', 'writing'].map(mod => {
                      const val = school[`avg_${mod}`];
                      const distAvg = summary[`district_avg_${mod}`] || 0;
                      const isAbove = val >= distAvg;
                      return (
                        <td key={mod} className="p-3 text-center">
                          <span className={`font-bold ${isAbove ? 'text-green-700' : 'text-red-600'}`}>{val}</span>
                          {school[`strongest_${mod}`] && <Award size={12} className="inline ml-1 text-yellow-500" />}
                          {school[`weakest_${mod}`] && <AlertTriangle size={12} className="inline ml-1 text-red-400" />}
                        </td>
                      );
                    })}
                    <td className="p-3 text-center">
                      <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 capitalize">{school.strongest_modality || '—'}</span>
                    </td>
                    <td className="p-3 text-center">
                      <span className="text-xs px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 capitalize">{school.weakest_modality || '—'}</span>
                    </td>
                  </tr>
                ))}
                {/* District Average Row */}
                <tr className="border-t-2 border-gray-300 bg-blue-50 font-bold">
                  <td className="p-3">District Average</td>
                  <td className="p-3 text-center">{summary.district_avg_listening || 0}</td>
                  <td className="p-3 text-center">{summary.district_avg_speaking || 0}</td>
                  <td className="p-3 text-center">{summary.district_avg_reading || 0}</td>
                  <td className="p-3 text-center">{summary.district_avg_writing || 0}</td>
                  <td className="p-3 text-center">—</td>
                  <td className="p-3 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ═══════════════ By Grade Level Tab ═══════════════ */}
        <TabsContent value="by-grade" data-testid="by-grade-content">
          <div className="mb-6">
            <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Grade-Level Performance Across Schools</h3>
            <p className="text-sm text-gray-500">See how each grade band performs at every school</p>
          </div>

          <div className="space-y-6">
            {gradePerformance?.grades?.map((grade) => (
              <div key={grade.grade_level} className="border-2 border-gray-200" data-testid={`grade-section-${grade.grade_level}`}>
                <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#002FA7] text-white flex items-center justify-center font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {grade.grade_level}
                    </div>
                    <div>
                      <h4 className="font-bold">Grade {grade.grade_level}</h4>
                      <span className="text-xs text-gray-500">{grade.total_exams} total assessments</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg text-[#002FA7]">{grade.avg_score}</div>
                    <div className="text-xs text-gray-500">District Avg</div>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {grade.schools?.filter(s => s.count > 0).map((school) => {
                    const diff = (school.avg_score - grade.avg_score).toFixed(1);
                    return (
                      <div key={school.school_id} className="flex items-center gap-4 p-4">
                        <div className="w-8 h-8 bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <School size={16} className="text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{school.school_name}</div>
                          <div className="text-xs text-gray-500">{school.count} exams</div>
                        </div>
                        <div className="flex-1">
                          <div className="h-2 bg-gray-100 relative">
                            <div className="absolute top-0 h-full w-0.5 bg-gray-400 z-10" style={{ left: `${(grade.avg_score / 6) * 100}%` }} title={`District: ${grade.avg_score}`} />
                            <div className={`h-full ${school.avg_score >= grade.avg_score ? 'bg-green-400' : 'bg-red-300'}`} style={{ width: `${(school.avg_score / 6) * 100}%` }} />
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 w-20">
                          <span className="font-bold">{school.avg_score}</span>
                          <span className={`text-xs ml-1 ${parseFloat(diff) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            ({parseFloat(diff) > 0 ? '+' : ''}{diff})
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {grade.schools?.filter(s => s.count > 0).length === 0 && (
                    <div className="p-6 text-center text-gray-400 text-sm">No schools have data for this grade level</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ═══════════════ Proficiency Distribution Tab ═══════════════ */}
        <TabsContent value="wida">
          <div className="border-2 border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-bold">Proficiency Levels — District-Wide</h3>
              <p className="text-sm text-gray-500">
                Distribution across all {summary.total_exams || 0} assessments
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border-t border-l border-gray-200">
              {[1, 2, 3, 4, 5, 6].map(level => {
                const count = stats?.wida_level_distribution?.[level] || 0;
                const total = stats?.total_exams || 1;
                const percentage = Math.round((count / total) * 100);
                
                return (
                  <div key={level} className="swiss-card text-center">
                    <div className={`w-16 h-16 mx-auto mb-3 flex items-center justify-center text-2xl font-bold ${getProficiencyLevelColor(level)}`}>
                      {level}
                    </div>
                    <div className="font-semibold">{WIDA_LEVEL_NAMES[level]}</div>
                    <div className="text-3xl font-bold text-[#002FA7] my-2">{count}</div>
                    <div className="text-sm text-gray-500">{percentage}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* ═══════════════ User Management Tab ═══════════════ */}
        <TabsContent value="users">
          <UserManagement initialRoleFilter={userRoleFilter} />
        </TabsContent>
        <TabsContent value="assignments" data-testid="assignments-content">
          <AssignmentManager schools={schools} level="district" />
        </TabsContent>
        <TabsContent value="licenses" data-testid="licenses-content">
          <LicenseManager />
        </TabsContent>
          </div>
        </div>
      </Tabs>
      {/* v6.34.15 — Smart install banner (hidden when already installed as PWA). */}
      <InstallAppBanner />
    </div>
  );
};

export default DistrictAdminDashboard;
